import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, catchError, filter, from, switchMap, take, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ErrorSnackBarComponent } from '../../shared/components/error-snack-bar/error-snack-bar.component';

function extractMessage(error: HttpErrorResponse): string {
  const body = error.error;
  if (typeof body === 'string' && body.length) return body;
  if (body && typeof body === 'object') {
    return body['message'] ?? body['detail'] ?? body['error'] ?? 'Something went wrong. Please try again.';
  }
  return 'Something went wrong. Please try again.';
}

// Shared across all interceptor calls so concurrent 401s share one refresh attempt
let refreshing = false;
const refreshDone$ = new BehaviorSubject<string | null>(null);

export const errorHandlerInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const snackBar = inject(MatSnackBar);
  const oauthService = inject(OAuthService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        if (refreshing) {
          // A refresh is already in flight — wait for the new token then retry
          return refreshDone$.pipe(
            filter((token): token is string => token !== null),
            take(1),
            switchMap(token => next(req.clone({
              setHeaders: {
                Authorization: `Bearer ${token}`,
                ...(environment.X_API_KEY ? { 'x-api-key': environment.X_API_KEY } : {}),
              },
            }))),
          );
        }

        // No refresh token in storage — skip the doomed refresh call and go straight to login
        if (!oauthService.getRefreshToken()) {
          oauthService.logOut();
          router.navigate(['/login']);
          return throwError(() => error);
        }

        refreshing = true;
        refreshDone$.next(null);

        return from(oauthService.refreshToken()).pipe(
          switchMap(() => {
            const token = oauthService.getAccessToken();
            refreshing = false;
            refreshDone$.next(token);
            return next(req.clone({
              setHeaders: {
                Authorization: `Bearer ${token}`,
                ...(environment.X_API_KEY ? { 'x-api-key': environment.X_API_KEY } : {}),
              },
            }));
          }),
          catchError(() => {
            refreshing = false;
            // logOut() only redirects if the discovery doc has an end_session_endpoint;
            // navigate as a fallback so the user always lands on /login.
            oauthService.logOut();
            router.navigate(['/login']);
            return throwError(() => error);
          }),
        );
      }

      if (error.status === 0) {
        // Network-level failure — silent
      } else if (error.status === 403) {
        const body = error.error;
        const errorCode: string = (body && typeof body === 'object' ? body['error_code'] : undefined) ?? 'AUTHORIZATION_DENIED';
        const message: string = (body && typeof body === 'object' ? body['message'] : undefined) ?? '';
        router.navigate(['/no-permission'], { state: { errorCode, message } });
      } else {
        snackBar.openFromComponent(ErrorSnackBarComponent, {
          data: { message: extractMessage(error) },
          duration: 5000,
          panelClass: ['error-snackbar'],
          horizontalPosition: 'right',
          verticalPosition: 'top',
        });
      }

      return throwError(() => error);
    }),
  );
};
